import React, { useEffect, useMemo } from 'react';
import {
  Checkbox,
  FormControl,
  FormGroup,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  Skeleton,
} from '@mui/material';

import { FormControlLabelDense } from '@upstart/commander-components';
import { CheckboxSrcType, ShareDealDialogDealConfig } from './types';
import styles from './Content.module.scss';
import messages from '../../messages.json';

export interface DealConfigSectionProps<V extends CheckboxSrcType> {
  config: ShareDealDialogDealConfig<V>;
  onChange: (newConfig: ShareDealDialogDealConfig<V>) => void;
}

const DealConfigSection = <V extends CheckboxSrcType>({
  config,
  onChange,
}: DealConfigSectionProps<V>) => {
  const { stepsSource, includedSteps, loadingSteps, startStep } = config;
  const handleCheckbox = (value: V, newCheckedValue: boolean) => {
    const newIncludedSteps = newCheckedValue
      ? [...includedSteps, value]
      : includedSteps.filter((step) => step !== value);

    onChange({
      ...config,
      includedSteps: newIncludedSteps,
    });
  };

  const handleRadio = (newStartStep: V) => {
    onChange({
      ...config,
      startStep: newStartStep,
    });
  };

  const includedStepsSet = useMemo(
    () => new Set(includedSteps),
    [includedSteps],
  );
  const isOnlyOneCheckboxChecked = includedSteps.length === 1;

  useEffect(() => {
    // while users are initializing includedSteps they will pass an empty array
    // which will always fail the following check
    if (loadingSteps) {
      return;
    }
    if (!includedSteps.includes(config.startStep)) {
      // When a disabled radio becomes selected we need to change
      // it to the first enabled one
      onChange({
        ...config,
        startStep: includedSteps[0],
      });
    }
  }, [includedSteps, onChange, config, loadingSteps]);

  const renderBodyContent = () => (
    <>
      <FormControl>
        <FormLabel component="legend">
          {messages.formStep.dealIncludedStepsSubheader}
        </FormLabel>
        <FormGroup>
          {stepsSource.map((src) => (
            <FormControlLabelDense
              key={src.value}
              value={src.value}
              label={src.label}
              control={
                <Checkbox
                  color="secondary"
                  checked={includedStepsSet.has(src.value)}
                  disabled={
                    isOnlyOneCheckboxChecked && includedStepsSet.has(src.value)
                  }
                  onChange={(e) => handleCheckbox(src.value, e.target.checked)}
                />
              }
            />
          ))}
        </FormGroup>
      </FormControl>

      <FormControl>
        <FormLabel component="legend">
          {messages.formStep.dealStartStepSubheader}
        </FormLabel>
        <RadioGroup
          value={startStep}
          onChange={(_e, newValue) => handleRadio(newValue as V)}
        >
          {stepsSource.map((src) => (
            <FormControlLabelDense
              disabled={!includedStepsSet.has(src.value)}
              key={src.value}
              value={src.value}
              label={src.label}
              control={<Radio color="secondary" />}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </>
  );

  return (
    <Paper
      className={`${styles.contentSection} ${styles.controlGroup}`}
      elevation={0}
      role="group"
      aria-label={messages.formStep.dealConfigSectionAriaLabel}
    >
      <Typography className={styles.paddingBottomMd} variant="h6">
        {messages.formStep.dealConfigHeader}
      </Typography>

      <div className={styles.dealLinkBody}>
        {loadingSteps && (
          <Skeleton
            variant="rectangular"
            className={styles.dealLinkSkeleton}
            sx={{ height: '200px' }}
            data-testid="skeleton"
          />
        )}
        {!loadingSteps && renderBodyContent()}
      </div>
    </Paper>
  );
};

export default DealConfigSection;
